import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  getProject,
  listSurveys,
  createSurvey,
  updateSurveyStatus,
  updateSurveyMode,
  updateProject,
  uploadLogo,
  deleteProject,
  deleteSurvey,
  updateSurveyName,
} from "../api";
import type { Project, Survey, ProjectBranding } from "../api";
import { useTitle } from "../hooks/useTitle";

const API_URL = import.meta.env.VITE_API_URL as string;
const SURVEY_URL = import.meta.env.VITE_SURVEY_URL as string | undefined;

export default function ProjectPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  useTitle(project?.name ?? "Chargement...");
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    async function load() {
      try {
        const [proj, srvs] = await Promise.all([
          getProject(projectId!),
          listSurveys(projectId!),
        ]);
        if (!cancelled) {
          setProject(proj);
          setSurveys(srvs.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError("Impossible de charger le projet.");
          setLoading(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [projectId]);

  async function handleToggleStatus(survey: Survey) {
    const newStatus = survey.status === "active" ? "inactive" : "active";
    try {
      const updated = await updateSurveyStatus(projectId!, survey.id, newStatus);
      setSurveys((prev) =>
        prev.map((s) => (s.id === updated.id ? updated : s))
      );
    } catch {
      // ignore
    }
  }

  async function handleToggleMode(survey: Survey) {
    const newMode = survey.mode === "production" ? "test" : "production";
    try {
      const updated = await updateSurveyMode(projectId!, survey.id, newMode);
      setSurveys((prev) =>
        prev.map((s) => (s.id === updated.id ? updated : s))
      );
    } catch {
      // ignore
    }
  }

  async function handleDeleteSurvey(survey: Survey) {
    const confirm = window.confirm(
      `Êtes-vous sûr de vouloir supprimer le sondage "${survey.name}" ?`
    );
    if (!confirm) return;
    try {
      await deleteSurvey(projectId!, survey.id);
      setSurveys((prev) => prev.filter((s) => s.id !== survey.id));
    } catch {
      alert("Erreur lors de la suppression du sondage.");
    }
  }

  async function handleRenameSurvey(survey: Survey) {
    const newName = window.prompt("Nouveau nom du sondage :", survey.name);
    if (!newName || newName === survey.name) return;
    try {
      const updated = await updateSurveyName(projectId!, survey.id, newName);
      setSurveys((prev) =>
        prev.map((s) => (s.id === updated.id ? { ...s, name: updated.name } : s))
      );
    } catch {
      alert("Erreur lors du renommage du sondage.");
    }
  }

  async function handleDeleteProject() {
    const confirm = window.confirm(
      `Êtes-vous sûr de vouloir supprimer le projet "${project?.name}" ? Tous les sondages et réponses seront définitivement supprimés.`
    );
    if (!confirm) return;
    try {
      await deleteProject(projectId!);
      navigate("/");
    } catch {
      alert("Erreur lors de la suppression du projet.");
    }
  }

  async function handleRenameProject() {
    const newName = window.prompt("Nouveau nom du projet :", project?.name);
    if (!newName || newName === project?.name) return;
    try {
      const updated = await updateProject(projectId!, { name: newName });
      setProject(updated);
    } catch {
      alert("Erreur lors du renommage du projet.");
    }
  }

  function handleSurveyCreated(survey: Survey) {
    setSurveys((prev) => [survey, ...prev]);
  }

  async function handleUpdateBranding(branding: ProjectBranding) {
    if (!projectId) return;
    try {
      const updated = await updateProject(projectId, { branding });
      setProject(updated);
    } catch (err) {
      alert("Erreur lors de la mise à jour du branding.");
    }
  }

  async function handleUploadLogo(file: File) {
    if (!projectId) return;
    try {
      const { logoUrl } = await uploadLogo(projectId, file);
      // Important: update local state AND server
      const newBranding = { ...project?.branding, logoUrl };
      await handleUpdateBranding(newBranding);
    } catch (err) {
      console.error(err);
      alert("Erreur lors du téléchargement du logo.");
    }
  }

  if (loading) return <p className="status-msg">Chargement…</p>;
  if (error) return <p className="status-msg error">{error}</p>;

  return (
    <main className="project-page">
      <nav className="breadcrumb">
        <Link to="/">Tableau de bord</Link>
        <span>›</span>
        <span>{project?.name ?? projectId}</span>
      </nav>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0 }}>{project?.name ?? projectId}</h1>
        <button type="button" onClick={handleRenameProject} style={{ padding: '4px 8px', fontSize: '0.8rem' }}>
          Renommer
        </button>
        <button type="button" onClick={handleDeleteProject} style={{ padding: '4px 8px', fontSize: '0.8rem', color: '#d9534f', borderColor: '#d43f3a' }}>
          Supprimer
        </button>
      </div>

      <div className="project-grid">
        <div className="main-content">
          <section className="card">
            <h2>Sondages</h2>
            {surveys.length === 0 ? (
              <p className="empty">Aucun sondage. Créez-en un ci-dessous.</p>
            ) : (
              <table className="survey-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Statut</th>
                    <th>Mode</th>
                    <th>Réponses</th>
                    <th>Créé le</th>
                  </tr>
                </thead>
                <tbody>
                  {surveys.map((s) => (
                    <SurveyRow
                      key={s.id}
                      survey={s}
                      projectId={projectId!}
                      onToggle={handleToggleStatus}
                      onToggleMode={handleToggleMode}
                      onDelete={handleDeleteSurvey}
                      onRename={handleRenameSurvey}
                      surveyPagePath={`/projects/${projectId}/surveys/${s.id}`}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="card">
            <h2>Créer un sondage</h2>
            <CreateSurveyForm projectId={projectId!} onCreated={handleSurveyCreated} />
          </section>
        </div>

        <aside className="sidebar">
          <section className="card">
            <h2>Branding</h2>
            <BrandingForm
              branding={project?.branding}
              onUpdate={handleUpdateBranding}
              onUploadLogo={handleUploadLogo}
            />
          </section>
        </aside>
      </div>
    </main>
  );
}

function BrandingForm({
  branding,
  onUpdate,
  onUploadLogo,
}: {
  branding?: ProjectBranding;
  onUpdate: (branding: ProjectBranding) => Promise<void>;
  onUploadLogo: (file: File) => Promise<void>;
}) {
  const [form, setForm] = useState<ProjectBranding>(() => ({
    primaryColor: "#f2513c",
    secondaryColor: "#a0d348",
    accentColor: "#2098ae",
    neutralColor: "#ffffff",
    logoUrl: "",
    fontFamily: "",
    ...branding,
  }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Sync internal state when prop changes (after upload)
  useEffect(() => {
    if (branding) {
      setForm(prev => ({ ...prev, ...branding }));
    }
  }, [branding]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploading(true);
      await onUploadLogo(file);
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    await onUpdate(form);
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="branding-form">
      <div className="form-row">
        <label>Logo</label>
        {form.logoUrl && (
          <div className="logo-preview">
            <img src={form.logoUrl} alt="Logo" style={{ maxWidth: "100%", maxHeight: "80px", objectFit: "contain", marginBottom: "10px", background: "#eee", padding: "5px", borderRadius: "4px" }} />
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          disabled={uploading}
        />
        {uploading && <small>Téléchargement...</small>}
      </div>
      <div className="form-row">
        <label>Couleur Primaire</label>
        <div className="color-input-wrapper">
          <input
            type="color"
            name="primaryColor"
            value={form.primaryColor || "#f2513c"}
            onChange={handleChange}
          />
          <input
            type="text"
            name="primaryColor"
            value={form.primaryColor || "#f2513c"}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="form-row">
        <label>Couleur Secondaire</label>
        <div className="color-input-wrapper">
          <input
            type="color"
            name="secondaryColor"
            value={form.secondaryColor || "#a0d348"}
            onChange={handleChange}
          />
          <input
            type="text"
            name="secondaryColor"
            value={form.secondaryColor || "#a0d348"}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="form-row">
        <label>Accent</label>
        <div className="color-input-wrapper">
          <input
            type="color"
            name="accentColor"
            value={form.accentColor || "#2098ae"}
            onChange={handleChange}
          />
          <input
            type="text"
            name="accentColor"
            value={form.accentColor || "#2098ae"}
            onChange={handleChange}
          />
        </div>
      </div>
      <div className="form-row">
        <label>Police (Font Family)</label>
        <input
          type="text"
          name="fontFamily"
          value={form.fontFamily || ""}
          onChange={handleChange}
          placeholder="sans-serif"
        />
      </div>
      <button type="submit" className="btn-primary" disabled={saving || uploading}>
        {saving ? "Enregistrement..." : "Enregistrer les couleurs"}
      </button>
    </form>
  );
}

function SurveyRow({
  survey,
  projectId,
  onToggle,
  onToggleMode,
  onDelete,
  onRename,
  surveyPagePath,
}: {
  survey: Survey;
  projectId: string;
  onToggle: (s: Survey) => void;
  onToggleMode: (s: Survey) => void;
  onDelete: (s: Survey) => void;
  onRename: (s: Survey) => void;
  surveyPagePath: string;
}) {
  const [toggling, setToggling] = useState(false);
  const [togglingMode, setTogglingMode] = useState(false);

  async function handleClick() {
    setToggling(true);
    await onToggle(survey);
    setToggling(false);
  }

  async function handleModeClick() {
    setTogglingMode(true);
    await onToggleMode(survey);
    setTogglingMode(false);
  }

  const previewUrl = SURVEY_URL
    ? survey.slug
      ? `${SURVEY_URL}/${survey.slug}`
      : `${SURVEY_URL}/${projectId}/${survey.id}`
    : null;

  return (
    <>
      <tr>
        <td><Link to={surveyPagePath}>{survey.name}</Link></td>
        <td>
          <span className={`badge badge-${survey.status}`}>
            {survey.status === "active" ? "Actif" : "Inactif"}
          </span>
        </td>
        <td>
          <span className={`badge badge-${survey.mode}`}>{survey.mode}</span>
        </td>
        <td className="count">{survey.responseCount}</td>
        <td>{new Date(survey.createdAt).toLocaleDateString("fr-CA")}</td>
      </tr>
      <tr className="survey-actions-row">
        <td colSpan={5}>
          <div className="row-actions">
            {previewUrl && (
              <a href={previewUrl} target="_blank" rel="noreferrer">
                <button type="button">Pré-visualiser</button>
              </a>
            )}
            <button type="button" onClick={handleClick} disabled={toggling}>
              {survey.status === "active" ? "Désactiver" : "Activer"}
            </button>
            <button type="button" onClick={handleModeClick} disabled={togglingMode}>
              {survey.mode === "production" ? "→ Test" : "→ Production"}
            </button>
            <a href={`${API_URL}/projects/${projectId}/surveys/${survey.id}/export/xlsx`} download>
              <button type="button">xlsx</button>
            </a>
            <button type="button" onClick={() => onRename(survey)}>
              Renommer
            </button>
            <button type="button" onClick={() => onDelete(survey)} style={{ color: '#d9534f' }}>
              Supprimer
            </button>
          </div>
        </td>
      </tr>
    </>
  );
}

function CreateSurveyForm({
  projectId,
  onCreated,
}: {
  projectId: string;
  onCreated: (s: Survey) => void;
}) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [mode, setMode] = useState<"production" | "test">("production");
  const [defJson, setDefJson] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!defJson.trim()) {
      setError("La définition SurveyJS est requise.");
      return;
    }

    let definition: unknown;
    try {
      definition = JSON.parse(defJson);
    } catch {
      setError("JSON invalide.");
      return;
    }

    setSubmitting(true);
    try {
      const created = await createSurvey(projectId, { name, slug, definition, mode });
      onCreated(created);
      setName("");
      setSlug("");
      setDefJson("");
      nameRef.current?.focus();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de la création.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="create-form">
      <div className="form-row">
        <label htmlFor="surveyName">Nom</label>
        <input
          id="surveyName"
          ref={nameRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nom du sondage"
        />
      </div>
      <div className="form-row">
        <label htmlFor="surveySlug">Slug (URL personnalisée)</label>
        <input
          id="surveySlug"
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="ex: sondage-2024"
        />
      </div>
      <div className="form-row">
        <label htmlFor="surveyMode">Mode</label>
        <select
          id="surveyMode"
          value={mode}
          onChange={(e) => setMode(e.target.value as "production" | "test")}
        >
          <option value="production">Production</option>
          <option value="test">Test</option>
        </select>
      </div>
      <div className="form-row">
        <label htmlFor="surveyDef">Définition SurveyJS (JSON)</label>
        <textarea
          id="surveyDef"
          rows={4}
          value={defJson}
          onChange={(e) => setDefJson(e.target.value)}
        />
      </div>
      {error && <p className="form-error">{error}</p>}
      <button type="submit" className="btn-primary" disabled={submitting}>
        {submitting ? "Création…" : "Créer le sondage"}
      </button>
    </form>
  );
}
